import React, { useEffect, useState } from 'react'
import { ButtonPrimary } from '@klarna/bubble-ui'

import PaymentInformationGeneric from './PaymentInformationGeneric/PaymentInformationGeneric'
import PaymentCalculator from './PaymentCalculator/PaymentCalculator'
import PaymentCalculatorRevamped from './PaymentCalculatorRevamped/PaymentCalculator'
import TimelineBased from './TimelineBased/TimelineBased'
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary'
import ErrorWithContext from './components/ErrorBoundary/ErrorWithContext'
import getValueFromNodes from './utilities/getValueFromNode'
import { Node } from './types'
import fetchContent from './utilities/fetchContent'
import removeBespokePathFromLocale from './utilities/removeBespokePathFromLocale'
import Layout from './components/Layout/Layout'
import sendInterstitialEvent from '../tracking/sendInterstitialEvent/sendInterstitialEvent'
import GkdAuthParams from '../types/GkdAuthParams'
import { ImpressionEvent } from '../../src/types/ImpressionEvent'
import filterNodesByTriggeringPlacement from './utilities/filterNodesByTriggeringPlacement'
import Deals from './Deals/Deals'
import { waitForVisibility } from '../utils/waitForVisibility/waitForVisibility'

type InterstitialProps = {
  theme: 'dark' | 'default'
  onClose: () => void
  bespokeId?: string
  locale: string
  adServerUrl: string
  placementKey: string
  legacyOSMClientId?: string
  customPaymentMethodIds: string[]
  trackerParameters?: ImpressionEvent
  interstitialTriggerer?: string
  focusTrapActive: boolean
  showButtons?: boolean
  intents?: string[]
  sendMessage?: (data: any, message: string) => void
} & GkdAuthParams;

function getInterstitial(bespokeId: string, nodes: Node[], theme: string, locale?: string, intents?: string[]) {
  switch (bespokeId) {
    case 'payment-information-generic-interstitial':
      return <PaymentInformationGeneric nodes={nodes} theme={theme} />
    case 'payment-calculator-interstitial':
      return <PaymentCalculator nodes={nodes} theme={theme} />
    case 'simple-timeline-based-interstitial':
      return <TimelineBased nodes={nodes} theme={theme} />
    case 'deals-interstitial':
      return <Deals nodes={nodes} theme={theme}/>
    case 'payment-calculator-interstitial-revamped':
      return <PaymentCalculatorRevamped nodes={nodes} theme={theme} locale={locale} intents={intents} />
    default:
      return null
  }
}

// eslint-disable-next-line @typescript-eslint/naming-convention
const Interstitial: React.FC<InterstitialProps> = props => {
  const {
    theme, onClose, bespokeId = 'default', showButtons, sendMessage, intents
  } = props
  const [content, setContent] = useState({ nodes: [] })
  const promoCodes = props?.customPaymentMethodIds?.join() || ''

  const [error, setError] = useState<ErrorWithContext | null>(null)

  useEffect(() => {
    const fetchContentArgs = {
      ...props,
      locale: removeBespokePathFromLocale(
        props.locale,
        props.bespokeId
      ),
      customPaymentMethodIds: promoCodes,
      intents
    }

    // The visibility gate observed a zero-area [data-loader] div, which could delay
    // the fetch until the parent's post-load resize. Cap it so a hidden iframe can't stall the fetch.
    const visibilityCapMs = 300
    Promise.race([
      waitForVisibility(),
      new Promise<void>(resolve => {
        setTimeout(resolve, visibilityCapMs)
      }),
    ])
      .then(() => fetchContent(fetchContentArgs))
      .then(data => {
        const dataWithFilteredNodes = filterNodesByTriggeringPlacement(data, props?.interstitialTriggerer)
        setContent(dataWithFilteredNodes)
        sendInterstitialEvent(props.locale, 'interstitial_view', {
          ...props,
          type: 'learn-more',
          bespokeId: props.bespokeId,
          k: props?.trackerParameters?.k,
        })
      })
      .catch(setError)
  }, [props])

  if (error) {
    throw error // This will trigger the error boundary
  }

  const footerButtons = []

  if (onClose && showButtons) {
    const closeButton = {
      Component: ButtonPrimary,
      props: {
        children: getValueFromNodes(content.nodes, 'CLOSE_BUTTON'),
        loading: false,
        id: 'learn-more-close-button',
        onClick: () => {
          onClose()
        },
      },
    }
    footerButtons.push(closeButton)
  }

  return (
    <Layout
      theme={theme}
      onClose={showButtons ? onClose : null}
      bespokeId={bespokeId}
      footerButtons={footerButtons}
      focusTrapActive={props.focusTrapActive}
      contentLoaded={content.nodes.length > 0}
      sendMessage={sendMessage}
    >
      {getInterstitial(bespokeId, content.nodes, theme, props.locale, intents)}
    </Layout>
  )
}

export default (props: InterstitialProps) => (
  <ErrorBoundary {...props}>
    <Interstitial {...props} />
  </ErrorBoundary>
)
